/* Laley core. One panel shell for every overlay view, and a cart count that follows the cart.
   No polling and no observers: everything reacts to events. */
// Only validated private views expose the exact, versioned module URL. Public pages do not import it.
const sharedAccessModuleUrl = document.getElementById('laley-shared-access-context')?.dataset.moduleUrl;
const { privateAccessUrl } = sharedAccessModuleUrl
  ? await import(sharedAccessModuleUrl)
  : { privateAccessUrl: (url) => url };
const panel = document.getElementById('panel');
const panelBody = document.getElementById('panel-body');
const panelTitle = document.getElementById('panel-title');
let returnFocus = null;
let closeTimer;
const still = matchMedia('(prefers-reduced-motion: reduce)');

// Cart consumers can invalidate a snapshot before a theme-owned write starts.
let cartMutationId = 0;
async function mutateCart(request) {
  const id = `theme:${++cartMutationId}`;
  document.dispatchEvent(new CustomEvent('cart:pending', { detail: { pending: true, id } }));
  try {
    return await request();
  } finally {
    document.dispatchEvent(new CustomEvent('cart:pending', { detail: { pending: false, id } }));
  }
}

// Product pages load this engine alongside core. Other pages wait for a purchase intent or an inserted form.
const formEngineUrl = document.querySelector('script[data-product-form-url]')?.dataset.productFormUrl;
let formEngine;
const productForms = () => {
  const ready = customElements.get('product-form');
  if (ready) return Promise.resolve(ready);
  if (!formEngineUrl) return Promise.reject(new Error('Missing product form URL'));
  return (formEngine ??= import(formEngineUrl)
    .then(() => {
      const ready = customElements.get('product-form');
      if (!ready) throw new Error('Product form was not registered');
      return ready;
    })
    .catch((error) => {
      formEngine = null;
      throw error;
    }));
};
// A panel can paint while the engine travels. Keep a fast submit for the upgrade, or the native form on failure.
const nativeForms = new WeakSet();
const pendingForms = new WeakSet();
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!form.closest('product-form') || customElements.get('product-form') || nativeForms.delete(form)) return;
    event.preventDefault();
    if (pendingForms.has(form)) return;
    pendingForms.add(form);
    const submit = () => {
      pendingForms.delete(form);
      if (form.isConnected) form.requestSubmit(event.submitter?.isConnected ? event.submitter : undefined);
    };
    productForms().then(submit, () => {
      nativeForms.add(form);
      submit();
    });
  },
  { capture: true },
);

/* Keep every open panel within the visible viewport as browser controls or the keyboard move. */
let measuredWidth = 0;
let measuredHeaderTop = 0;
const measure = (force = false) => {
  const viewport = window.visualViewport;
  const headerTop = viewport?.scale === 1 ? Math.max(0, Math.round(viewport.offsetTop)) : 0;
  if (headerTop !== measuredHeaderTop) {
    measuredHeaderTop = headerTop;
    document.documentElement.style.setProperty('--lly-header-top', `${headerTop}px`);
  }
  if (!force && innerWidth === measuredWidth) return;
  measuredWidth = innerWidth;
  const height = Math.round(window.visualViewport?.height || innerHeight);
  document.documentElement.style.setProperty('--lly-screen', `${height}px`);
  document.documentElement.style.setProperty(
    '--lly-viewport-top',
    `${Math.round(window.visualViewport?.offsetTop || 0)}px`,
  );
};
measure();
const resizePanel = () => measure(panel.open);
addEventListener('resize', resizePanel);
addEventListener('pageshow', resizePanel);
window.visualViewport?.addEventListener('resize', resizePanel);
window.visualViewport?.addEventListener('scroll', resizePanel);

/* Keep an undecoded purchase image on its plate; cached pictures never disappear on a panel refresh. */
function preparePanelImages(root) {
  root
    .querySelectorAll('.quick-picture img, .thumb img, .upsell-picture img, .offer-set-media img, .swatch img')
    .forEach((image) => {
      if ((image.complete && image.naturalWidth > 0) || image.hasAttribute('data-panel-image')) return;
      image.dataset.panelImage = 'loading';
      const clear = () => {
        image.removeEventListener('load', reveal);
        image.removeEventListener('error', failed);
      };
      const failed = () => {
        if (!image.isConnected) return clear();
        image.dataset.panelImage = 'failed';
      };
      const reveal = async () => {
        const source = image.currentSrc;
        await image.decode().catch(() => {});
        if (!image.isConnected || !panel.contains(image) || !panel.open || 'closing' in panel.dataset) return clear();
        if (image.currentSrc !== source || !image.complete) return;
        if (image.naturalWidth > 0) {
          clear();
          delete image.dataset.panelImage;
        } else failed();
      };
      image.addEventListener('load', reveal);
      image.addEventListener('error', failed);
      if (image.complete) image.naturalWidth > 0 ? reveal() : failed();
    });
}

/** Open a view: a <template data-view="name"> anywhere in the page, or a template element. */
export function openPanel(view, { side = 'end', trigger = null } = {}) {
  const template = typeof view === 'string' ? document.querySelector(`template[data-view="${view}"]`) : view;
  if (!template) return;
  if (template.content.querySelector('product-form')) productForms().catch(() => {});
  panelBody.removeAttribute('aria-busy');
  panelBody.replaceChildren(template.content.cloneNode(true));
  preparePanelImages(panelBody);
  enhancePanelLinks(panelBody);
  panelTitle.textContent = template.dataset.title || '';
  stopClosing();
  delete panel.dataset.closing;
  panel.dataset.side = side;
  panel.dataset.view = template.dataset.view || '';
  measure(true);
  // A tab inside the panel keeps the focus target of the first opening.
  if (!panel.open) returnFocus = trigger || document.activeElement;
  if (!panel.open) panel.showModal();
  document.documentElement.style.overflow = 'hidden';
  panelBody.scrollTop = 0;
  (panelBody.querySelector('[data-autofocus]') || panelTitle).focus({ preventScroll: true });
  panel.dispatchEvent(new CustomEvent('panel:open', { bubbles: true, detail: { view: panel.dataset.view } }));
}

function stopClosing() {
  clearTimeout(closeTimer);
  panel.removeEventListener('animationend', finishClosing);
}

function finishClosing(event) {
  if (event && event.target !== panel) return;
  stopClosing();
  if (!('closing' in panel.dataset)) return;
  delete panel.dataset.closing;
  panel.close();
}

/** Close after the motion, with a fallback when a browser cancels the animation. */
export function closePanel() {
  if (!panel.open || 'closing' in panel.dataset) return;
  // Dismissing a sheet also cancels a checkout that was waiting for a cart write.
  finishCartCheckout(true);
  if (still.matches) return panel.close();
  panel.dataset.closing = '';
  panel.addEventListener('animationend', finishClosing);
  closeTimer = setTimeout(finishClosing, 240);
}

// Escape closes the same way.
panel.addEventListener('cancel', (event) => {
  event.preventDefault();
  closePanel();
});

panel.addEventListener('close', () => {
  // The native close event is queued; a newer view may already have reopened this dialog.
  if (panel.open) return;
  finishCartCheckout(true);
  stopClosing();
  delete panel.dataset.closing;
  document.documentElement.style.overflow = '';
  panelBody.replaceChildren();
  // Components hidden by the panel restore their visibility in their own close handlers first.
  const focus = returnFocus;
  requestAnimationFrame(() => {
    if (!panel.open && returnFocus === focus && focus?.isConnected) focus.focus({ preventScroll: true });
  });
  refreshNativeCart();
});

panel.addEventListener('click', (event) => {
  if (event.target === panel || event.target.closest('[data-panel-close]')) closePanel();
});

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-panel-open]');
  if (!trigger) return;
  event.preventDefault();
  openPanel(trigger.dataset.panelOpen, { side: trigger.dataset.panelSide || 'end', trigger });
});

// These links remain ordinary destinations without JS. With the panel available they act as buttons, including
// Space from the keyboard. Run again on section replacements, whose links have just come from Shopify.
function enhancePanelLinks(root = document) {
  root.querySelectorAll('a[data-panel-open]').forEach((link) => link.setAttribute('role', 'button'));
}
enhancePanelLinks();
document.addEventListener('shopify:section:load', ({ target }) => {
  enhancePanelLinks(target);
  syncFavorites();
  if (target.querySelector('product-form')) productForms().catch(() => {});
});
document.addEventListener('keydown', (event) => {
  if (event.key !== ' ' || !event.target.matches('a[data-panel-open]')) return;
  event.preventDefault();
  if (!event.repeat) event.target.click();
});

let cartCountRevision = 0;
function setCartCount(count) {
  cartCountRevision++;
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = Number(count) || '';
  });
}
export async function refreshCartCount() {
  const revision = ++cartCountRevision;
  const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  }).then((response) => (response.ok ? response.json() : Promise.reject(response)));
  if (revision === cartCountRevision) setCartCount(cart.item_count);
  return cart;
}

document.addEventListener('cart:changed', () => refreshCartCount().catch(() => {}));

const root = window.Shopify?.routes?.root || '/';

/* A quick quantity change must reach Shopify before the native checkout follows it. Keep one intent while
   cart writes settle, then replay it through the normal handlers (including stale Merchant-attribute cleanup). */
const pendingCartWrites = new Set();
const pendingCartButtons = new Map();
const pendingCartWrappers = new Map();
function syncCartPendingButtons() {
  if (pendingCartWrites.size) {
    // The SDK owns button.disabled and may update it asynchronously; the theme owns this temporary UI lock.
    for (const wrapper of document.querySelectorAll('.hurtigkasse-cart')) {
      if (!pendingCartWrappers.has(wrapper)) pendingCartWrappers.set(wrapper, wrapper.inert);
      wrapper.inert = true;
    }
    for (const button of document.querySelectorAll('.hurtigkasse-cart .hurtigkasse-btn')) {
      if (!pendingCartButtons.has(button))
        pendingCartButtons.set(button, { disabled: button.disabled, busy: button.getAttribute('aria-busy') === 'true' });
      button.disabled = true;
    }
    return;
  }
  for (const [button, state] of pendingCartButtons) {
    // Leave an in-progress vendor request locked. Its own handler restores the button when it finishes.
    if (button.getAttribute('aria-busy') !== 'true') button.disabled = state.busy ? false : state.disabled;
  }
  pendingCartButtons.clear();
  for (const [wrapper, inert] of pendingCartWrappers) wrapper.inert = inert;
  pendingCartWrappers.clear();
}
let waitingCheckout;
let replayingCheckout = false;
let nativeCartStale = false;
let nativeCartRefreshing = false;
let checkoutStarting = false;
function gateNativeCart() {
  const form = document.querySelector('.cart-form');
  if (!form) return;
  // A closed drawer must never expose quantities that can overwrite its still-pending change.
  form.inert = true;
  form.setAttribute('aria-busy', 'true');
  form
    .querySelectorAll('[name="updates[]"], [name="update"], [name="checkout"], [data-cart-change]')
    .forEach((control) => {
      if ('disabled' in control) control.disabled = true;
      else control.setAttribute('aria-disabled', 'true');
    });
  const page = form.closest('[data-cart-page]');
  const note = page?.querySelector('[data-cart-error]');
  if (note && page.dataset.cartUpdating) {
    note.setAttribute('role', 'status');
    note.textContent = page.dataset.cartUpdating;
    note.hidden = false;
  }
}
function refreshNativeCart() {
  // Keep the old form gated while its replacement document is still travelling.
  if (
    !nativeCartStale ||
    nativeCartRefreshing ||
    panel.open ||
    pendingCartWrites.size ||
    waitingCheckout ||
    checkoutStarting
  )
    return;
  if (!document.querySelector('.cart-form')) return;
  nativeCartRefreshing = true;
  location.reload();
}
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  checkoutStarting = false;
  nativeCartRefreshing = false;
  refreshNativeCart();
});
function finishCartCheckout(cancel = false) {
  if (!waitingCheckout || (!cancel && pendingCartWrites.size)) return;
  const intent = waitingCheckout;
  waitingCheckout = null;
  clearTimeout(intent.timeout);
  if (intent.busy == null) intent.control.removeAttribute('aria-busy');
  else intent.control.setAttribute('aria-busy', intent.busy);
  if (cancel) return;
  replayingCheckout = true;
  checkoutStarting = true;
  try {
    if (intent.resume() === false) checkoutStarting = false;
  } finally {
    replayingCheckout = false;
  }
}
document.addEventListener('cart:pending', (event) => {
  const { id, pending } = event.detail || {};
  if (typeof id !== 'string' || !id || typeof pending !== 'boolean') return;
  if (pending) {
    pendingCartWrites.add(id);
    if (panel.open && document.querySelector('.cart-form')) {
      nativeCartStale = true;
      gateNativeCart();
    }
  } else pendingCartWrites.delete(id);
  syncCartPendingButtons();
  if (!pendingCartWrites.size)
    queueMicrotask(() => {
      finishCartCheckout();
      refreshNativeCart();
    });
});
function holdCartCheckout(event, control, resume) {
  if (!pendingCartWrites.size && !waitingCheckout) {
    checkoutStarting = true;
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  if (waitingCheckout) return;
  waitingCheckout = { control, resume, busy: control.getAttribute('aria-busy') };
  control.setAttribute('aria-busy', 'true');
  // Release a stalled intent for a retry; never navigate while its quantity change is still uncertain.
  waitingCheckout.timeout = setTimeout(() => finishCartCheckout(true), 15000);
}
const checkoutPath = new URL(`${root}checkout`, location.origin).pathname.replace(/\/$/, '');
const cartPath = new URL(`${root}cart`, location.origin).pathname.replace(/\/$/, '');
// Window capture runs before the retired Merchant cleanup's document capture. Native replays still reach it.
window.addEventListener(
  'click',
  (event) => {
    if (nativeCartStale && event.target.closest?.('.cart-form')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      gateNativeCart();
      return;
    }
    if (
      replayingCheckout ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const link = event.target.closest?.('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== checkoutPath) return;
    holdCartCheckout(event, link, () => {
      if (link.isConnected && link.href === url.href) return link.click();
      // A completed quantity response may have replaced the drawer and its original checkout link.
      const replacement = document.createElement('a');
      replacement.href = url.href;
      replacement.hidden = true;
      document.body.append(replacement);
      replacement.click();
      replacement.remove();
    });
  },
  { capture: true },
);
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    const submitter = event.submitter;
    // Includes Enter/requestSubmit without a named submitter. Never replay a stale updates[] form.
    if (form instanceof HTMLFormElement && nativeCartStale && form.matches('.cart-form')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      gateNativeCart();
      return;
    }
    if (
      replayingCheckout ||
      event.defaultPrevented ||
      !(form instanceof HTMLFormElement) ||
      submitter?.name !== 'checkout'
    )
      return;
    const action = new URL(submitter.getAttribute('formaction') || form.action, location.href);
    const target = submitter.getAttribute('formtarget') || form.target;
    if (
      action.origin !== location.origin ||
      ![cartPath, checkoutPath].includes(action.pathname.replace(/\/$/, '')) ||
      (target && target !== '_self')
    )
      return;
    holdCartCheckout(event, submitter, () => {
      if (!form.isConnected || !submitter.isConnected) return false;
      form.requestSubmit(submitter);
    });
  },
  { capture: true },
);

/* Sections rendered by Shopify (Section Rendering), fetched once per page view: a hover or focus on «Legg til»
   starts quick add before the click, and what goes with a product is ready when it is added. */
const sections = new Map();
const load = (url, id) => {
  url = privateAccessUrl(url);
  const key = `${url}${url.includes('?') ? '&' : '?'}section_id=${id}`;
  if (!sections.has(key))
    sections.set(
      key,
      fetch(key)
        .then((response) => (response.ok ? response.text() : Promise.reject(response)))
        .catch((error) => {
          sections.delete(key);
          throw error;
        }),
    );
  return sections.get(key);
};
const sectionHtml = async (url, id) => new DOMParser().parseFromString(await load(url, id), 'text/html');

/** Quick add: the product's own form in the panel. What goes with the product is fetched along with it. Opened from
   the bag, an add goes back to the bag (showAdded). */
let quickFrom = '';
let quickIntent = 0;
const quickPosts = new Map();
export async function openQuick(url, trigger) {
  const intent = ++quickIntent;
  const current = () =>
    intent === quickIntent && panel.open && panel.dataset.view === 'quick-add' && !('closing' in panel.dataset);
  if (!panel.open || panel.dataset.view !== 'quick-add') quickFrom = panel.open ? panel.dataset.view : '';
  // The sheet opens at once with the product's name (a phone has no hover to fetch it early) and fills in when the
  // form comes; if it cannot come, the product's page opens instead.
  const shell = document.createElement('template');
  shell.dataset.view = 'quick-add';
  shell.dataset.title = trigger?.closest('.area')?.querySelector('.area-link')?.textContent.trim() || '';
  const pending = Promise.all([sectionHtml(url, 'quick-add'), productForms()])
    .then(([html]) => html)
    .catch(() => null);
  openPanel(shell, { trigger });
  // Busy until the form fills the sheet, or «Lagt til» takes its place.
  panelBody.setAttribute('aria-busy', 'true');
  try {
    const html = await pending;
    // A later tap or a closed/replaced view cancels this intent, including its failure fallback.
    if (!current()) return;
    const template = html?.querySelector('template[data-view="quick-add"]');
    if (!template) return (location.href = url);
    // Nothing to choose (one variant, no plans): the tap is the add, and «Lagt til» takes the sheet's place, as V3.
    const now = template.content.querySelector('product-form[data-at-once] form');
    let refused = null;
    if (now) {
      // Repeated taps may reach a cached form after its first add has already started. Share only that add.
      const variant = new FormData(now).get('id') || new URL(url, location.href).href;
      if (!quickPosts.has(variant)) quickPosts.set(variant, customElements.get('product-form').post(now));
      const posting = quickPosts.get(variant);
      let result;
      try {
        result = await posting;
      } finally {
        if (quickPosts.get(variant) === posting) quickPosts.delete(variant);
      }
      const { ok, item } = result;
      if (!current()) {
        // An accepted add cannot be undone by closing the sheet; sync the bag without reopening an old view.
        if (ok) document.dispatchEvent(new CustomEvent('cart:changed'));
        return;
      }
      if (ok) return showAdded(item, trigger);
      refused = item;
    }
    // On the product's own page «Se alle detaljer» leads nowhere new.
    const handle = (path) => path.split('?')[0].split('/products/')[1];
    if (handle(new URL(url, location.href).pathname) === handle(location.pathname))
      template.content.querySelector('.quick-more')?.remove();
    openPanel(template, { trigger });
    // Refused: the sheet says what Shopify said on its line, as a refusal after a pick does.
    const note = refused && panelBody.querySelector('[data-stock]');
    if (note)
      note.textContent =
        (typeof refused.description === 'string' && refused.description) || refused.message || note.dataset.failed;
  } finally {
    if (intent === quickIntent && panel.dataset.view === 'quick-add') panelBody.removeAttribute('aria-busy');
  }
  load(url, 'upsell').catch(() => {});
}
const warm = (event) => {
  const quick = event.target.closest?.('[data-quick-add]');
  if (quick) {
    productForms().catch(() => {});
    load(quick.dataset.quickAdd, 'quick-add').catch(() => {});
  }
  const dot = event.target.closest?.('[data-product-card] [data-sibling]');
  if (dot) load(dot.href, 'card').catch(() => {});
  if (event.target.closest?.('[data-cart-open]')) {
    productForms().catch(() => {});
  }
};
document.addEventListener('pointerover', warm, { passive: true });
document.addEventListener('touchstart', warm, { passive: true });
document.addEventListener('focusin', warm);
document.addEventListener('click', (event) => {
  const quick = event.target.closest('[data-quick-add]');
  if (!quick) return;
  event.preventDefault();
  openQuick(quick.dataset.quickAdd, quick);
});

/* A colour dot in a card shows that colour in place: the sibling's own card from Shopify (sections/card.liquid),
   with the dots kept in their order and «+n» leading to the colour shown. The ring moves at once; the new card
   keeps the list's picture size and its badge left out (data-omit), and it comes in with its picture decoded, so
   the plate is never empty. A new tab or window still opens the colour's page, and so does a failed fetch. */
const cardSwaps = new WeakMap();
document.addEventListener('click', async (event) => {
  const dot = event.target.closest('[data-product-card] [data-sibling]');
  if (!dot || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  const card = dot.closest('[data-product-card]');
  const intent = {};
  cardSwaps.set(card, intent);
  const current = () => card.isConnected && cardSwaps.get(card) === intent;
  const colors = card.querySelector('.card-colors');
  colors
    .querySelectorAll('[data-sibling]')
    .forEach((link) =>
      link === dot ? link.setAttribute('aria-current', 'true') : link.removeAttribute('aria-current'),
    );
  card.setAttribute('aria-busy', 'true');
  const next = await sectionHtml(dot.href, 'card')
    .then((html) => html.querySelector('[data-product-card]'))
    .catch(() => null);
  // A slower earlier tap must neither replace the latest choice nor navigate on a stale failure.
  if (!current()) return;
  if (!next) {
    card.removeAttribute('aria-busy');
    return (location.href = dot.href);
  }
  const omit = card.dataset.omit;
  if (omit) {
    next.dataset.omit = omit;
    next.querySelector(`.badge[data-kind="${omit}"]`)?.remove();
  }
  const sizes = card.querySelector('.card-media img')?.sizes;
  next.querySelectorAll('.card-media img').forEach((img) => sizes && (img.sizes = sizes));
  const picture = next.querySelector('.card-media > img');
  if (picture) picture.loading = 'eager';
  document.adoptNode(next);
  await picture?.decode().catch(() => {});
  if (!current()) return;
  // The kept dots take the new card's name for their group.
  const fresh = next.querySelector('.card-colors');
  if (fresh) {
    colors.ariaLabel = fresh.ariaLabel;
    fresh.replaceWith(colors);
  }
  colors.querySelector('a:not([data-sibling])')?.setAttribute('href', dot.getAttribute('href'));
  card.removeAttribute('aria-busy');
  card.replaceWith(next);
  cardSwaps.delete(card);
  syncFavorites();
  dot.focus({ preventScroll: true });
});

// The added view is a visual rail. Cart and product-page recommendations keep their compact rows.
class AddedRecommendations extends HTMLElement {
  connectedCallback() {
    this.setup();
  }
  disconnectedCallback() {
    this.events?.abort();
  }
  setup() {
    this.events?.abort();
    const rail = this.querySelector('.added-rail');
    if (!rail) return;
    this.events = new AbortController();
    const { signal } = this.events;
    const nav = this.querySelector('.added-rail-nav');
    const cards = [...rail.children];
    const position = (card) => card.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      if (nav) {
        nav.hidden = max < 2;
        nav.querySelector('[data-added-step="-1"]').disabled = rail.scrollLeft <= 1;
        nav.querySelector('[data-added-step="1"]').disabled = rail.scrollLeft >= max - 1;
      }
    };
    const move = (step) => {
      const at = cards.reduce(
        (best, card, i) =>
          Math.abs(position(card) - rail.scrollLeft) < Math.abs(position(cards[best]) - rail.scrollLeft) ? i : best,
        0,
      );
      const next = cards[Math.max(0, Math.min(cards.length - 1, at + step))];
      if (next) rail.scrollTo({ left: position(next), behavior: still.matches ? 'instant' : 'smooth' });
    };
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-added-step]');
        if (button) move(Number(button.dataset.addedStep));
      },
      { signal },
    );
    rail.addEventListener(
      'keydown',
      (event) => {
        if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End')
          rail.scrollTo({
            left: event.key === 'Home' ? 0 : rail.scrollWidth,
            behavior: still.matches ? 'instant' : 'smooth',
          });
        else move(event.key === 'ArrowLeft' ? -1 : 1);
      },
      { signal },
    );
    rail.addEventListener(
      'focusin',
      (event) => {
        const card = event.target.closest('.added-card');
        if (!card) return;
        const left = position(card),
          right = left + card.getBoundingClientRect().width;
        if (left < rail.scrollLeft || right > rail.scrollLeft + rail.clientWidth)
          rail.scrollTo({ left: left < rail.scrollLeft ? left : right - rail.clientWidth, behavior: 'instant' });
      },
      { signal },
    );
    rail.addEventListener('scroll', update, { passive: true, signal });
    addEventListener('resize', update, { signal });
    let drag = null,
      draggedClick = false;
    rail.addEventListener(
      'pointerdown',
      (event) => {
        draggedClick = false;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('button, input, select, product-form, [data-quick-add]')) return;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, moved: false };
      },
      { signal },
    );
    rail.addEventListener(
      'pointermove',
      (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        if (!(event.buttons & 1)) {
          rail.classList.remove('is-dragging');
          drag = null;
          return;
        }
        const dx = event.clientX - drag.x,
          dy = event.clientY - drag.y;
        if (!drag.moved && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 6) {
          drag = null;
          return;
        }
        if (!drag.moved && Math.abs(dx) > 6) {
          drag.moved = true;
          rail.classList.add('is-dragging');
          rail.setPointerCapture(drag.id);
        }
        if (!drag.moved) return;
        event.preventDefault();
        rail.scrollLeft = drag.left - dx;
      },
      { signal },
    );
    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      draggedClick = drag.moved;
      if (rail.hasPointerCapture(drag.id)) rail.releasePointerCapture(drag.id);
      rail.classList.remove('is-dragging');
      drag = null;
    };
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'])
      rail.addEventListener(type, endDrag, { signal });
    rail.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    rail.addEventListener(
      'click',
      (event) => {
        if (!draggedClick || event.detail === 0) return;
        draggedClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { capture: true, signal },
    );
    requestAnimationFrame(() => {
      if (this.isConnected) update();
    });
  }
}
customElements.define('added-recommendations', AddedRecommendations);

/** Recommendations omit the bag's products/colours. Added has up to six visual cards; other panels three rows. */
function placeRows(rows, target, held) {
  if (rows.querySelector('product-form')) productForms().catch(() => {});
  const visual = target.matches('[data-added-upsell]');
  rows.querySelectorAll('[data-product-id]').forEach((row) => {
    if (held.has(row.dataset.productId)) row.remove();
  });
  rows.querySelectorAll('[data-product-id]').forEach((row, index) => index > (visual ? 5 : 2) && row.remove());
  rows.querySelectorAll('form[id]').forEach((form) => form.removeAttribute('id'));
  const heading = rows.querySelector('[data-panel-heading]');
  if (heading) heading.textContent = target.dataset.upsellHeading || heading.dataset.panelHeading;
  const cartLabel = target.closest('[data-cart-recommendations]')?.querySelector('[data-cart-upsell-label]');
  if (cartLabel && heading) cartLabel.textContent = heading.textContent;
  if (rows.querySelector('[data-product-id]')) {
    if (visual) {
      rows.querySelectorAll('[data-product-id]').forEach((row) => {
        const picture = row.querySelector('template[data-upsell-picture]');
        if (picture) {
          row.querySelector('.thumb')?.replaceWith(picture.content.cloneNode(true));
          picture.remove();
        }
        row.classList.add('added-card');
        // Keep the native form/size chooser, with its action visible on the photo at every screen size.
        const image = row.querySelector('.upsell-picture');
        const action = row.querySelector(':scope > product-form, :scope > [data-quick-add]');
        if (image && action) {
          const media = document.createElement('div');
          media.className = 'added-media';
          image.replaceWith(media);
          action.classList.add('added-add');
          const button = action.matches('[data-quick-add]') ? action : action.querySelector('[data-buy]');
          const plus = document.createElement('span');
          plus.className = 'added-add-icon';
          plus.setAttribute('aria-hidden', 'true');
          plus.textContent = '+';
          button?.prepend(plus);
          media.append(image, action);
        }
      });
      const rail = rows.querySelector('.product-rows');
      rail.classList.add('added-rail');
      rail.id = 'added-upsell-rail';
      rail.tabIndex = 0;
      if (heading) {
        heading.id = 'added-upsell-heading';
        rail.setAttribute('aria-labelledby', heading.id);
        const head = document.createElement('div');
        head.className = 'added-rail-heading';
        heading.replaceWith(head);
        head.append(heading);
        const navigation = target.querySelector('template[data-upsell-navigation]');
        if (navigation) head.append(navigation.content.cloneNode(true));
      }
    }
    target.replaceChildren(...rows.childNodes);
    if (panel.contains(target)) preparePanelImages(target);
    target.setup?.();
  }
}

/** What goes with a product (sections/upsell.liquid): its pick, its list, then small add-ons. */
export async function upsellFor(product, target, held = new Set()) {
  const rows = (await sectionHtml(product.url, 'upsell')).querySelector('[data-upsell]');
  if (rows && target.isConnected) placeRows(rows, target, held);
}

function syncOfferFrequency(frequency) {
  const control = frequency.closest('.offer-frequency');
  const display = control?.querySelector('[data-offer-frequency-display]');
  const label = display?.querySelector('[data-offer-frequency-label]');
  const option = frequency.selectedOptions[0];
  if (!label || !option) return;
  label.textContent = option.textContent.trim();
  display.hidden = false;
  control.dataset.frequencyReady = '';
}

/** «Lagt til»: the line just added (small, at the top), the offer it may have (the set, or the subscription) as a
   small box under it, the free-shipping meter and what goes with it, all from the section Shopify renders with the
   add (sections/added.liquid), so nothing more is asked for; on the product page its own rows are used at once. The
   bag's buttons are always there: the offer is a choice, not a step. */
export function showAdded(item, trigger) {
  // Added from inside the bag (a row there, or quick add opened there): the bag shows the new line, as V3.
  const from = panel.dataset.view === 'quick-add' ? quickFrom : panel.dataset.view;
  const cart = panel.open && from === 'cart' && cartView(item.sections?.['cart-drawer'] || '');
  if (cart) {
    openPanel(cart);
    return fillCart(cart);
  }
  const bag =
    item.sections?.added &&
    new DOMParser().parseFromString(item.sections.added, 'text/html').querySelector('[data-bag]');
  if (bag) setCartCount(bag.dataset.count);
  else document.dispatchEvent(new CustomEvent('cart:changed'));
  const template = document.querySelector('template[data-view="added"]').cloneNode(true);
  const view = template.content;
  const line = bag?.querySelector(`template[data-line="${CSS.escape(item.key)}"]`);
  if (line) view.querySelector('[data-added-line]').replaceChildren(line.content.cloneNode(true));
  const showCart = () => {
    const drawer = cartView(item.sections?.['cart-drawer'] || '');
    if (!drawer) return openCart(trigger);
    openPanel(drawer, { trigger });
    return fillCart(drawer);
  };
  if ([7993145098342, 7993145131110, 8186572865638].includes(Number(item.product_id))) return showCart();
  const offer = bag?.querySelector(`template[data-offer="${CSS.escape(item.key)}"]`);
  if (offer) view.querySelector('[data-added-offer]').replaceChildren(offer.content.cloneNode(true));
  const frequency = view.querySelector('[data-offer-frequency]');
  if (frequency) syncOfferFrequency(frequency);
  openPanel(template, { trigger });
  // Follow the item just added, so an unrelated, more expensive cart line cannot change its recommendation context.
  const target = panelBody.querySelector('[data-added-upsell]');
  const held = new Set(bag?.dataset.held.split(','));
  const anchor = String(item.product_id);
  const own = document.querySelector(`[data-upsell-source="${anchor}"]`);
  const showCartIfEmpty = () => {
    if (offer || !target.isConnected || !panel.open || panel.dataset.view !== 'added' || target.querySelector('[data-product-id]')) return;
    showCart();
  };
  if (own?.querySelector('[data-product-id]')) {
    placeRows(own.cloneNode(true), target, held);
    showCartIfEmpty();
  } else upsellFor({ url: item.url.split('?')[0] }, target, held).then(showCartIfEmpty, showCartIfEmpty);
}

/* The bag as a panel (sections/cart-drawer.liquid), opened from the bag icon and «Handlekurv». − + and «Fjern» change
   it in place with one call each (cart/change.js renders the view along), and the focus stays where it was. */
const cartView = (html) =>
  new DOMParser().parseFromString(html, 'text/html').querySelector('template[data-view="cart"]');
const cartScroller = () => panelBody.querySelector('[data-cart-scroll]') || panelBody;
/* Read the current server-rendered cart on each open: eligibility, quantities and prices can change while
   the bag is closed, including writes performed by an app. */
const loadBag = () =>
  fetch(`${root}?section_id=cart-drawer`, { cache: 'no-store' })
    .then((response) => (response.ok ? response.text() : Promise.reject(response)));
function fillCart(template, syncCount = true) {
  syncCartPendingButtons();
  preparePanelImages(panelBody);
  panelTitle.textContent = template.dataset.title || panelTitle.textContent;
  if (syncCount) setCartCount(template.dataset.count);
  const target = panelBody.querySelector('[data-cart-upsell]');
  if (target) upsellFor({ url: target.dataset.cartUpsell }, target, new Set(template.dataset.held.split(',')));
}
export async function openCart(trigger) {
  productForms().catch(() => {});
  trigger?.setAttribute('aria-busy', 'true');
  const html = await loadBag().catch(() => '');
  trigger?.removeAttribute('aria-busy');
  const template = html && cartView(html);
  if (!template) return (location.href = trigger?.href || `${root}cart`);
  openPanel(template, { trigger });
  fillCart(template);
}

/* Shopify supplies the action runtime. Configure only its UI hooks; Shopify still validates/writes the cart and
   emits the standard events once. App mutations refresh an already open cart without reopening a dismissed view. */
let actionRevision = 0;
function configureActions() {
  const actions = window.Shopify?.actions;
  actions?.openCart?.configure({ handler: () => openCart(document.activeElement) });
  actions?.updateCart?.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const revision = ++actionRevision;
      const countRevision = cartCountRevision;
      const view = panelBody.firstElementChild;
      const current = () =>
        revision === actionRevision &&
        panel.open &&
        panel.dataset.view === 'cart' &&
        !('closing' in panel.dataset) &&
        panelBody.firstElementChild === view;
      const result = await mutateCart(defaultHandler);
      if (countRevision === cartCountRevision && result.cart) setCartCount(result.cart.totalQuantity);
      const count = refreshCartCount().catch(() => {});
      if (current()) {
        const html = await loadBag().catch(() => '');
        const template = html && cartView(html);
        if (template && current()) {
          const active = document.activeElement;
          const focus = panelBody.contains(active);
          const line = active.closest('[data-line]')?.dataset.line;
          const selector = active.id
            ? `#${CSS.escape(active.id)}`
            : line && active.dataset.control
              ? `[data-line="${CSS.escape(line)}"] [data-control="${CSS.escape(active.dataset.control)}"]`
              : null;
          const scroll = cartScroller().scrollTop;
          panelBody.replaceChildren(template.content.cloneNode(true));
          enhancePanelLinks(panelBody);
          // The count follows the action/fresh cart, never a potentially older section response.
          fillCart(template, false);
          cartScroller().scrollTop = scroll;
          if (focus) ((selector && panelBody.querySelector(selector)) || panelTitle).focus({ preventScroll: true });
        }
      }
      await count;
      return result;
    },
  });
}
// Head modules can run at readyState=interactive before Shopify's later body module has executed.
if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', configureActions, { once: true });
configureActions();

let changingCartLine = false;
function cartChangeError(scope, message = '') {
  const note = scope?.querySelector('[data-cart-error]');
  if (!note) return;
  note.textContent = message || note.dataset.failed;
  note.hidden = false;
}
async function changeLine(button) {
  // A line's position changes as soon as another line is removed. Use its key, and finish one view update before
  // accepting another tap. Never allow the native remove link to run while an Ajax change is already underway.
  if (changingCartLine || pendingCartWrites.size || !button.isConnected) return;
  const originalRow = button.closest('[data-line-key]');
  const key = originalRow?.dataset.lineKey;
  const quantity = Number(button.dataset.cartChange);
  const page = button.closest('[data-cart-page]');
  const scope = page || panelBody;
  if (!key || !Number.isInteger(quantity) || quantity < 0 || (page && !page.dataset.cartSection))
    return cartChangeError(scope);
  const title = originalRow.querySelector('.row-title').textContent.trim();
  const control = button.dataset.control;
  const view = panelBody.firstElementChild;
  const current = () =>
    page ? page.isConnected : panel.open && panel.dataset.view === 'cart' && panelBody.firstElementChild === view;
  const section = page?.dataset.cartSection;
  // Disable the full page's old updates[] fields/submitters until its freshly rendered form has replaced them.
  const locked = [
    ...scope.querySelectorAll('[data-cart-change], [name="updates[]"], [name="update"], [name="checkout"], .hurtigkasse-btn'),
  ].map((control) => {
    const state = {
      control,
      disabled: control.disabled,
      ariaDisabled: control.getAttribute('aria-disabled'),
      vendor: control.matches('.hurtigkasse-btn'),
      busy: control.getAttribute('aria-busy') === 'true',
    };
    if ('disabled' in control) control.disabled = true;
    else control.setAttribute('aria-disabled', 'true');
    return state;
  });
  changingCartLine = true;
  button.setAttribute('aria-busy', 'true');
  const oldNote = scope.querySelector('[data-cart-error]');
  if (oldNote) oldNote.hidden = true;
  let shown = scope;
  let failure = '';
  const render = async (html) => {
    if (!current()) return false;
    if (page) {
      const replacement = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-cart-page]');
      if (!replacement) throw new Error('Missing cart section');
      page.replaceWith(replacement);
      shown = replacement;
      setCartCount(replacement.dataset.count);
      // The refreshed native form now follows the same cart as its drawer.
      nativeCartStale = false;
      return true;
    }
    const template = cartView(html || '');
    if (!template) throw new Error('Missing cart section');
    const scroll = cartScroller().scrollTop;
    panelBody.replaceChildren(template.content.cloneNode(true));
    cartScroller().scrollTop = scroll;
    fillCart(template);
    return true;
  };
  const refresh = async () => {
    const url = page ? `${root}cart?section_id=${encodeURIComponent(section)}` : `${root}?section_id=cart-drawer`;
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw response;
    return render(await response.text());
  };
  try {
    // Pending covers the parsed response and fresh view too, so checkout cannot submit a stale native cart form.
    await mutateCart(async () => {
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: section ? ['cart-drawer', section] : 'cart-drawer' }),
        });
        const cart = await response.json();
        if (!response.ok) {
          failure = typeof cart.description === 'string' ? cart.description : '';
          throw response;
        }
        setCartCount(cart.item_count);
        const html = cart.sections?.[section || 'cart-drawer'];
        if (html) await render(html);
        else await refresh();
      } catch (error) {
        // A stale/missing key or an uncertain network response recovers the cart in place; never navigate to
        // Shopify's error page, and never retry a potentially completed write automatically.
        finishCartCheckout(true);
        let recovered = false;
        try {
          recovered = await refresh();
        } catch {
          // The original controls remain available for an explicit retry when the connection returns.
        }
        // A repeated/old remove can reach Shopify after that exact item has already gone. The fresh cart is the
        // source of truth: when the requested removal is already complete, show its normal successful state.
        if (!(recovered && quantity === 0 && !shown.querySelector(`[data-line-key="${CSS.escape(key)}"]`))) {
          if (page ? shown.isConnected : panel.open && panel.dataset.view === 'cart') cartChangeError(shown, failure);
          return;
        }
      }
      if (page) {
        const heading = shown.querySelector('h1');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
        return;
      }
      if (!panel.open || panel.dataset.view !== 'cart') return;
      // After «Fjern», focus the title rather than the next item's remove button under the same finger.
      const row = control === 'remove' ? null : panelBody.querySelector(`[data-line-key="${CSS.escape(key)}"]`);
      const said = panel.querySelector('[data-panel-said]');
      const total = panelBody.querySelector('.cart-total span:last-child')?.textContent.trim() || '';
      const count = row?.querySelector('.qty-stepper span')?.textContent.trim() || '';
      said.textContent = row
        ? said.dataset.quantity.replace('[count]', count).replace('[total]', total)
        : said.dataset.removed.replace('[title]', title);
      (
        row?.querySelector(`[data-control="${control}"]:not(:disabled)`) ||
        row?.querySelector('[data-control]:not(:disabled)') ||
        panelTitle
      ).focus({ preventScroll: true });
    });
  } finally {
    changingCartLine = false;
    button.removeAttribute('aria-busy');
    for (const { control, disabled, ariaDisabled, vendor, busy } of locked) {
      if ('disabled' in control) {
        if (!vendor) control.disabled = disabled;
        else if (control.getAttribute('aria-busy') !== 'true') control.disabled = busy ? false : disabled;
      }
      if (ariaDisabled == null) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', ariaDisabled);
    }
  }
}
document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-cart-open]');
  if (open) {
    event.preventDefault();
    openCart(open);
    return;
  }
  const change = event.target.closest('[data-cart-change]');
  if (!change || (!panelBody.contains(change) && !change.closest('[data-cart-page]'))) return;
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return;
  event.preventDefault();
  if (change.getAttribute('aria-busy') !== 'true') changeLine(change).catch(() => change.removeAttribute('aria-busy'));
});

/* A code to copy (snippets/copy-code.liquid, a collection's banner): one press copies it, and the label says so for a
   moment. */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-copy]');
  if (!button) return;
  const label = button.querySelector('[data-copy-label]');
  try {
    await navigator.clipboard.writeText(button.dataset.copy);
  } catch {
    // In-app browsers (Instagram, Facebook) often refuse the clipboard API; a selected field copies there.
    const field = Object.assign(document.createElement('textarea'), { value: button.dataset.copy, readOnly: true });
    field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.append(field);
    field.select();
    const copied = document.execCommand('copy');
    field.remove();
    if (!copied) return;
  }
  label.dataset.before ??= label.textContent;
  label.textContent = button.dataset.copied;
  clearTimeout(button.copyTimer);
  button.copyTimer = setTimeout(() => (label.textContent = label.dataset.before), 2000);
});

/* Discount codes in the bag and on the cart page (snippets/cart-discount.liquid). Shopify keeps the codes on the cart
   (cart/update.js «discount», where a /discount/KODE link puts its code too), so a change sends the codes the cart has
   now, plus the new one or minus the removed one, and the fresh view comes along. A code that does not apply says so. */
async function setCartDiscounts(box, change, added = '') {
  const page = box.closest('[data-cart-page]');
  const section = page?.dataset.cartSection;
  if ((page && !section) || box.getAttribute('aria-busy') === 'true') return;
  box.setAttribute('aria-busy', 'true');
  const say = (scope, text) => {
    const note = scope?.querySelector('[data-discount-note]');
    if (note && text) Object.assign(note, { textContent: text, hidden: false });
  };
  try {
    await mutateCart(async () => {
      const current = await fetch(`${root}cart.js`, { cache: 'no-store' }).then((response) => response.json());
      const codes = change((current.discount_codes || []).map((entry) => entry.code));
      if (!codes) return;
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: section ? ['cart-drawer', section] : 'cart-drawer' }),
      });
      const cart = await response.json();
      if (!response.ok) throw response;
      const state = added && cart.discount_codes?.find((entry) => entry.code.toLowerCase() === added.toLowerCase());
      const html = cart.sections?.[section || 'cart-drawer'] || '';
      let shown = null;
      if (page) {
        const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page]');
        if (replacement && page.isConnected) {
          page.replaceWith(replacement);
          nativeCartStale = false;
          shown = replacement;
        }
      } else if (panel.open && panel.dataset.view === 'cart') {
        const template = cartView(html);
        if (template) {
          const scroll = cartScroller().scrollTop;
          panelBody.replaceChildren(template.content.cloneNode(true));
          cartScroller().scrollTop = scroll;
          fillCart(template);
          shown = panelBody;
        }
      }
      if (state && !state.applicable) say(shown, box.dataset.notApplicable);
    });
  } catch {
    say(box, box.dataset.failed);
  } finally {
    box.removeAttribute('aria-busy');
  }
}
document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-discount-form]');
  if (!form) return;
  event.preventDefault();
  const code = form.elements.discount.value.trim();
  if (!code) return;
  setCartDiscounts(
    form.closest('[data-cart-discount]'),
    (codes) => [...codes.filter((entry) => entry.toLowerCase() !== code.toLowerCase()), code],
    code,
  );
});
document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-discount-remove]');
  if (!remove) return;
  const code = remove.dataset.discountRemove.toLowerCase();
  setCartDiscounts(remove.closest('[data-cart-discount]'), (codes) =>
    codes.filter((entry) => entry.toLowerCase() !== code),
  );
});

const campaignAutoCode = document.querySelector('[data-campaign-auto-code]');
if (campaignAutoCode) {
  const code = campaignAutoCode.dataset.campaignAutoCode.trim();
  if (code)
    setCartDiscounts(
      campaignAutoCode,
      (codes) => codes.some((entry) => entry.toLowerCase() === code.toLowerCase()) ? null : [...codes, code],
      code,
    );
}

const offerMoney = (cents) => `${Math.round(cents / 100).toLocaleString('nb-NO')}\u00a0kr`;
function setOfferChoice(offer) {
  const data = JSON.parse(offer.querySelector('[data-offer-variants]').textContent);
  const picked = [];
  offer.querySelectorAll('[data-offer-option]').forEach((option, index) => {
    picked[Number(option.dataset.offerOption ?? index)] = option.value;
  });
  const at = picked.length
    ? data.options.findIndex(
        (values) => values.length === picked.length && values.every((value, i) => value === picked[i]),
      )
    : data.ids.findIndex((id) => String(id) === offer.dataset.variant);
  const price = data.prices[at];
  const sourcePrice = Number(offer.dataset.sourcePrice);
  const sourceReference = Number(offer.dataset.sourceReference ?? offer.dataset.sourcePrice);
  const separately = (data.separately?.[at] ?? Number(offer.dataset.separately)) + sourcePrice - sourceReference;
  const reference = data.reference?.[at] ?? separately;
  const free = at >= 0 && data.available[at] && price < separately;
  const quantity = Number(offer.dataset.quantity);
  const extra = (price - sourcePrice) * quantity;
  const saving = offer.querySelector('[data-set-saving]');
  if (saving) {
    saving.hidden = !free;
    saving.textContent = free
      ? saving.dataset.template.replace('[amount]', offerMoney((separately - price) * quantity))
      : '';
  }
  if (at >= 0) {
    offer.dataset.variant = data.ids[at];
    offer.dataset.reference = reference;
    offer.querySelector('[data-set-price]').textContent = offerMoney(price * quantity);
    const totalLabel = offer.querySelector('[data-set-total-label]');
    if (totalLabel)
      totalLabel.textContent =
        Number(offer.dataset.parts) === 2 && quantity === 1 ? offer.dataset.pairLabel : offer.dataset.totalLabel;
    const before = offer.querySelector('[data-set-before]');
    if (before) {
      before.hidden = reference <= price;
      const amount = before.querySelector('[data-set-reference]');
      if (amount) amount.textContent = offerMoney(reference * quantity);
      const label = before.querySelector('[data-set-reference-label]');
      if (label)
        label.textContent = data.compare?.[at] > price ? offer.dataset.beforeLabel : offer.dataset.separatelyLabel;
    }
  } else {
    offer.querySelector('[data-set-price]').textContent = '—';
  }
  const extraText = offer.querySelector('[data-set-extra]');
  if (extraText) {
    extraText.hidden = !free;
    const template =
      extra > 0
        ? extraText.dataset.templatePos
        : extra === 0
          ? extraText.dataset.templateFree
          : extraText.dataset.templateLess;
    extraText.textContent = free ? template.replace('[amount]', offerMoney(Math.abs(extra))) : '';
  }
  const count = offer.querySelector('[data-set-quantity]');
  if (count) {
    count.hidden = quantity <= 1;
    count.textContent = count.dataset.template.replace('[count]', quantity);
  }
  const take = offer.querySelector('[data-offer-take]');
  const action =
    extra > 0 ? take.dataset.actionExtra : extra === 0 ? take.dataset.actionFree : take.dataset.actionUpgrade;
  if (action) take.textContent = action.replace('[amount]', offerMoney(Math.abs(extra)));
  take.disabled = !free;
  const message = offer.querySelector('[data-offer-message]');
  message.textContent = free
    ? ''
    : at >= 0 && data.available[at]
      ? message.dataset.notSaving || message.dataset.unavailable
      : message.dataset.unavailable;
  message.hidden = free;
  return free;
}

/* The subscription keeps the chosen frequency, variant and current quantity. Shopify requires a line index for
   selling_plan, so resolve the offer's key against a fresh bag before changing it. The maximum saving adds the
   conditional freight benefit once to the quantity-total product discount; the breakdown keeps both explicit. */
document.addEventListener('change', (event) => {
  const frequency = event.target.closest('[data-offer-frequency]');
  if (frequency) {
    const offer = frequency.closest('.offer');
    const option = frequency.selectedOptions[0];
    syncOfferFrequency(frequency);
    const money = (cents) => `${Math.round(cents / 100).toLocaleString('nb-NO')}\u00a0kr`;
    offer.dataset.plan = option.value;
    offer.querySelector('[data-offer-price]').textContent = money(
      Number(option.dataset.price) * Number(offer.dataset.quantity),
    );
    const saving = offer.querySelector('[data-offer-saving]');
    const discount = Number(option.dataset.saving);
    const shipping = Number(offer.dataset.offerShipping || 0);
    saving.textContent = saving.dataset.template
      .replace('[amount]', money(discount + shipping))
      .replace('[period]', option.dataset.period);
    const breakdown = offer.querySelector('[data-offer-breakdown]');
    if (breakdown)
      breakdown.textContent = breakdown.dataset.template
        .replace('[amount]', money(discount))
        .replace('[shipping]', money(shipping));
    offer.querySelector('[data-offer-unit]').textContent = option.dataset.period;
    offer.querySelector('[data-offer-message]').hidden = true;
    return;
  }
  const select = event.target.closest('[data-offer-option]');
  if (!select) return;
  setOfferChoice(select.closest('.offer'));
});
document.addEventListener('click', async (event) => {
  const take = event.target.closest('[data-offer-take]');
  if (!take || take.disabled) return;
  const offer = take.closest('.offer');
  const offerIsCurrent = () =>
    panel.open && panel.dataset.view === 'added' && !('closing' in panel.dataset) && panelBody.contains(offer);
  if (!offerIsCurrent()) return;
  const set = offer.dataset.offerKind === 'set';
  if (set && !setOfferChoice(offer)) return;
  const message = offer.querySelector('[data-offer-message]');
  const frequency = offer.querySelector('[data-offer-frequency]');
  const selectedPlan = offer.dataset.plan;
  const selectedVariant = offer.dataset.variant;
  const sizeControls = [...offer.querySelectorAll('[data-offer-option]')];
  const disabledSizes = sizeControls.map((control) => control.disabled);
  take.disabled = true;
  if (frequency) frequency.disabled = true;
  sizeControls.forEach((control) => (control.disabled = true));
  take.setAttribute('aria-busy', 'true');
  try {
    let current;
    let lineIndex;
    let updates;
    if (set) {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(response.status);
      const bag = await response.json();
      if (!offerIsCurrent()) return;
      current = bag.items.find((item) => item.key === offer.dataset.key);
      const plain = (item) => !item.selling_plan_allocation && !Object.keys(item.properties || {}).length;
      const targets = bag.items.filter((item) => String(item.variant_id) === selectedVariant);
      if (
        !current ||
        String(current.variant_id) !== offer.dataset.sourceVariant ||
        !plain(current) ||
        !Number.isInteger(current.quantity) ||
        current.quantity < 1 ||
        !Number.isInteger(current.final_price) ||
        current.final_price < 0 ||
        String(current.variant_id) === selectedVariant ||
        targets.length > 1 ||
        targets.some((item) => !plain(item) || !Number.isInteger(item.quantity) || item.quantity < 1)
      )
        throw new Error('offer changed');
      // A precise extra-price promise must still match the source line at the moment it is accepted.
      // Refresh a changed price/quantity first; a second click accepts the visible updated offer.
      if (
        current.quantity !== Number(offer.dataset.quantity) ||
        current.final_price !== Number(offer.dataset.sourcePrice)
      ) {
        offer.dataset.quantity = current.quantity;
        offer.dataset.sourcePrice = current.final_price;
        if (setOfferChoice(offer)) {
          message.textContent = message.dataset.updated || message.dataset.failed;
          message.hidden = false;
        }
        return;
      }
      const target = targets[0];
      updates = {
        [current.key]: 0,
        [target?.key || selectedVariant]: current.quantity + (target?.quantity || 0),
      };
    } else {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(response.status);
      const bag = await response.json();
      if (!offerIsCurrent()) return;
      lineIndex = bag.items.findIndex((item) => item.key === offer.dataset.key);
      current = bag.items[lineIndex];
      const unitPrice = Number(frequency.selectedOptions[0].dataset.price);
      if (
        !current ||
        current.selling_plan_allocation ||
        String(current.variant_id) !== offer.dataset.variant ||
        !Number.isInteger(current.quantity) ||
        current.quantity < 1 ||
        !Number.isInteger(current.final_price) ||
        current.final_price < 0
      )
        throw new Error('offer changed');
      if (
        current.quantity !== Number(offer.dataset.quantity) ||
        current.final_price !== Number(offer.dataset.sourcePrice)
      ) {
        // Shopify recalculates discount, freight benefit and supported plans for the new line. Showing the
        // refreshed offer is a separate step; only a second click may accept its new recurring amount.
        const refreshed = await fetch(`${root}?section_id=added`, {
          headers: { Accept: 'text/html' },
          cache: 'no-store',
        });
        if (!refreshed.ok) throw new Error(refreshed.status);
        const html = await refreshed.text();
        if (!offerIsCurrent()) return;
        const bag = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bag]');
        if (!bag?.querySelector(`template[data-line="${CSS.escape(current.key)}"]`))
          throw new Error('refreshed line missing');
        showAdded({ ...current, sections: { added: html } }, take);
        const updated = panelBody.querySelector('[data-added-offer] .offer');
        const updatedFrequency = updated?.querySelector('[data-offer-frequency]');
        if (
          [...(updatedFrequency?.options || [])].some((option) => option.value === selectedPlan && !option.disabled)
        ) {
          updatedFrequency.value = selectedPlan;
          updatedFrequency.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const updatedMessage = updated?.querySelector('[data-offer-message]');
        if (updatedMessage) {
          updatedMessage.textContent =
            updatedMessage.dataset.updated || message.dataset.updated || message.dataset.failed;
          updatedMessage.hidden = false;
        } else {
          const status = message.cloneNode(true);
          status.textContent = message.dataset.unavailable;
          status.hidden = false;
          panelBody.querySelector('[data-added-offer]')?.append(status);
        }
        return;
      }
      if (unitPrice * current.quantity >= current.final_line_price) throw new Error('offer changed');
    }
    // A closed or replaced offer no longer authorizes the pending read to become a purchase change.
    if (!offerIsCurrent()) return;
    const response = await mutateCart(() =>
      fetch(`${root}cart/${set ? 'update' : 'change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          set
            ? { updates, sections: 'added' }
            : {
                line: lineIndex + 1,
                quantity: current.quantity,
                selling_plan: Number(selectedPlan),
                sections: 'added',
              },
        ),
      }),
    );
    if (!response.ok) throw new Error(response.status);
    const cart = await response.json();
    if (!offerIsCurrent()) {
      // A late accepted change refreshes the current bag without replacing the customer's newer view.
      document.dispatchEvent(new CustomEvent('cart:changed'));
      return;
    }
    const line = cart.items.find((item) =>
      set
        ? String(item.variant_id) === selectedVariant && !item.selling_plan_allocation
        : String(item.variant_id) === offer.dataset.variant &&
          String(item.selling_plan_allocation?.selling_plan.id) === selectedPlan,
    );
    if (!line) throw new Error('not in the bag');
    showAdded({ ...line, sections: cart.sections }, take);
  } catch {
    if (offerIsCurrent()) {
      message.textContent = message.dataset.failed;
      message.hidden = false;
    }
    take.disabled = false;
  } finally {
    take.removeAttribute('aria-busy');
    if (!offerIsCurrent()) take.disabled = false;
    if (frequency) frequency.disabled = false;
    sizeControls.forEach((control, index) => (control.disabled = disabledSizes[index]));
  }
});

/* Favourites: kept in this browser only, so nothing is sent anywhere. Each heart carries what the list shows. */
const FAVORITES = 'laley:favorites';
const readFavorites = () => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES)) || [];
  } catch {
    return [];
  }
};
function syncFavorites() {
  const list = readFavorites();
  document.querySelectorAll('[data-favorite]').forEach((button) =>
    button.setAttribute(
      'aria-pressed',
      list.some((item) => item.handle === button.dataset.favorite),
    ),
  );
  document.querySelectorAll('[data-favorites-count]').forEach((badge) => {
    badge.textContent = list.length || '';
  });
  const target = panelBody.querySelector('[data-favorites-list]');
  if (!target) return;
  target.replaceChildren(
    ...list.map((item) => {
      const row = document.createElement('li');
      row.className = 'product-row product-row--favorite area';
      // As in the sketch: «Fjern» under the price, «Legg til» opens quick add with the product's choices.
      row.innerHTML =
        '<span class="thumb"><img alt="" width="64" height="64"></span><div class="row-copy"><a class="row-title area-link"></a><p class="price"></p></div><a class="button button--compact" data-quick-add><span></span><span class="sr-only"></span></a><button type="button" class="text-link text-link--quiet row-remove" data-favorite-remove></button>';
      row.querySelector('.row-title').href = item.url;
      row.querySelector('img').src = item.image;
      row.querySelector('.thumb').classList.toggle('thumb--packshot', !!item.packshot);
      row.querySelector('.row-title').textContent = item.title;
      row.querySelector('.price').textContent = item.price;
      const remove = row.querySelector('[data-favorite-remove]');
      remove.dataset.favoriteRemove = item.handle;
      remove.textContent = target.dataset.removeLabel || '×';
      const add = row.querySelector('[data-quick-add]');
      add.href = add.dataset.quickAdd = item.url;
      add.firstChild.textContent = target.dataset.addLabel;
      add.lastChild.textContent = `: ${item.title}`;
      return row;
    }),
  );
  preparePanelImages(target);
  panelBody.querySelector('[data-favorites-empty]').hidden = list.length > 0;
}
function setFavorites(list) {
  try {
    localStorage.setItem(FAVORITES, JSON.stringify(list));
  } catch {
    /* private mode: the heart still answers */
  }
  syncFavorites();
}
document.addEventListener('click', (event) => {
  const heart = event.target.closest('[data-favorite]');
  const remove = event.target.closest('[data-favorite-remove]');
  if (!heart && !remove) return;
  const list = readFavorites();
  const handle = heart ? heart.dataset.favorite : remove.dataset.favoriteRemove;
  const kept = list.filter((item) => item.handle !== handle);
  if (heart && kept.length === list.length) {
    const { title, url, image, price } = heart.dataset;
    kept.unshift({ handle, title, url, image, price, packshot: 'packshot' in heart.dataset });
  }
  setFavorites(kept);
});
panel.addEventListener('panel:open', syncFavorites);
syncFavorites();

/* Cookie consent through Shopify's Customer Privacy API: our banner shows only where and when Shopify says it must. */
const consent = document.querySelector('[data-consent-banner]');
function saveConsent({ analytics, marketing, preferences }) {
  window.Shopify?.customerPrivacy?.setTrackingConsent(
    { analytics, marketing, preferences, sale_of_data: marketing },
    () => {
      if (consent) consent.hidden = true;
      if (panel.dataset.view === 'consent') closePanel();
      document.dispatchEvent(new CustomEvent('consent:changed'));
    },
  );
}
if (consent) {
  window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) return;
    const privacy = window.Shopify.customerPrivacy;
    // shouldShowBanner says the region asks for consent; an earlier answer is in currentVisitorConsent.
    const answered = Object.values(privacy.currentVisitorConsent()).some(Boolean);
    consent.hidden = !privacy.shouldShowBanner() || answered;
    document.dispatchEvent(new CustomEvent('consent:changed'));
  });
  document.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-consent]')?.dataset.consent;
    if (choice)
      saveConsent({ analytics: choice === 'accept', marketing: choice === 'accept', preferences: choice === 'accept' });
  });
  document.addEventListener('submit', (event) => {
    if (!event.target.matches('[data-consent-form]')) return;
    event.preventDefault();
    const form = event.target;
    saveConsent({
      analytics: form.analytics.checked,
      marketing: form.marketing.checked,
      preferences: form.preferences.checked,
    });
  });
}

/* A picture's pointer says what a click does: a white circle with «+» over the product pictures and in the large
   view until it is zoomed, both arrows over the pictures of a rail that can move sideways (the one towards an end
   fades there). Mouse only; a card's own buttons keep their pointer. Inside the open dialog the circle moves in with
   it. It moves once a frame; what it shows is written only when that changes, and a card's picture and its rail are
   measured when the pointer comes to them, not on every move (again after a scroll, a click or a resize). */
const pointer = document.createElement('div');
pointer.className = 'cursor';
pointer.hidden = true;
pointer.setAttribute('aria-hidden', 'true');
pointer.innerHTML =
  '<svg viewBox="0 0 24 24" data-zoom><path d="M5 12h14M12 5v14"/></svg><svg viewBox="0 0 24 24" data-back><path d="M20 12H4m6-6-6 6 6 6"/></svg><svg viewBox="0 0 24 24" data-forward><path d="M4 12h16m-6-6 6 6-6 6"/></svg>';
document.body.append(pointer);
const [, backArrow, forwardArrow] = pointer.children;
const inside = (r, point) =>
  point.clientX >= r.left && point.clientX <= r.right && point.clientY >= r.top && point.clientY <= r.bottom;
const sideOf = (r, point) => (inside(r, point) ? 0 : point.clientX < r.left + r.width / 2 ? -1 : 1);
/** Beside a picture in the large view: -1 on its left, 1 on its right, 0 on the picture itself. */
export const besidePicture = (img, point) =>
  sideOf(img.closest('image-viewer')?.pictureBounds || img.getBoundingClientRect(), point);
let point = null; // the last mouse position
let frame = 0;
let shown = ''; // what the circle shows now
let over = null; // the card or zoomable picture under the pointer, with the card's picture box
let box = null;
let rail = null; // that card's rail: how far it moves and where it stands
let room = 0;
let left = 0;
let pointed = null; // the card whose picture the circle covers (no native pointer there)
const forget = () => {
  over = rail = box = null;
};
function aim(target) {
  const zoom = target?.closest?.('[data-cursor="zoom"]:not(.is-zoomed)');
  // Over a card the target is its name: the link reaches over the card, the circle is for its picture.
  const card = !zoom && target?.matches?.('.area-link') ? target.closest('[data-rail] [data-product-card]') : null;
  const now = zoom || card || null;
  if (now !== over) {
    over = now;
    box = card?.querySelector('.card-media').getBoundingClientRect();
    rail = card?.closest('[data-rail]') || null;
    room = rail ? rail.scrollWidth - rail.clientWidth : 0;
    left = rail?.scrollLeft || 0;
  }
  const slides = !!point && !!box && room > 1 && inside(box, point);
  // In the large view the picture zooms («+»); beside it the side the pointer is on steps (one arrow). Its picture is
  // measured by the viewer when its photo or viewport changes; a larger decoded file keeps the same geometry.
  const side = zoom?.matches('[data-viewer-image]') ? besidePicture(zoom.querySelector('img'), point) : 0;
  const pointing = slides ? card : null;
  if (pointing !== pointed) {
    pointed?.removeAttribute('data-pointing');
    pointing?.setAttribute('data-pointing', '');
    pointed = pointing;
  }
  const state = zoom ? `zoom ${side}` : slides ? `slide ${left <= 1} ${left >= room - 1}` : '';
  if (state === shown) return;
  shown = state;
  const host = over?.closest('dialog') || document.body;
  if (state && pointer.parentElement !== host) host.append(pointer);
  pointer.hidden = !state;
  if (!state) return;
  pointer.dataset.kind = zoom && !side ? 'zoom' : 'slide';
  pointer.dataset.side = side < 0 ? 'back' : side > 0 ? 'forward' : '';
  backArrow.toggleAttribute('data-end', !zoom && left <= 1);
  forwardArrow.toggleAttribute('data-end', !zoom && left >= room - 1);
}
const move = () => {
  frame = 0;
  if (point) pointer.style.transform = `translate(${point.clientX}px, ${point.clientY}px) translate(-50%, -50%)`;
};
const follow = () => {
  if (shown && !frame) frame = requestAnimationFrame(move);
};
document.addEventListener(
  'pointermove',
  (event) => {
    if (event.pointerType !== 'mouse') return;
    point = event;
    aim(event.target);
    follow();
  },
  { passive: true },
);
// A scroll moves the page or the rail under a still pointer; a click can zoom or step the picture; a resize moves all.
const again = () => {
  if (!point) return;
  forget();
  aim(document.elementFromPoint(point.clientX, point.clientY));
  follow();
};
document.addEventListener('scroll', again, { capture: true, passive: true });
// A captured gallery drag can end without a click or scroll (at an end); restore the pointer under the still mouse.
for (const type of ['click', 'pointerup', 'pointercancel', 'lostpointercapture'])
  document.addEventListener(type, () => requestAnimationFrame(again));
addEventListener('resize', again, { passive: true });
document.documentElement.addEventListener('pointerleave', () => {
  point = null;
  forget();
  aim(null);
});

/* The bar's departments, a disclosure each (WAI): the pointer opens a panel after a pause (header.css), the toggle after
   the name opens it from the keyboard at once. One panel at a time; focus leaving the department closes it; Escape
   closes it, or holds a panel the pointer opened shut until the pointer leaves, and focus in the panel goes back to
   the toggle. A department's panel is laid out, and its pictures load, once the pointer or the focus reaches it. */
const setOpen = (item, open) => {
  item.toggleAttribute('data-open', open);
  item.querySelector('.nav-toggle')?.setAttribute('aria-expanded', open);
};
const reach = (event) => {
  const bar = event.target.closest('.header-links');
  if (!bar) return;
  const item = event.target.closest('.nav-item');
  if (!item) return;
  item.toggleAttribute('data-reached', true);
  bar.querySelectorAll('.nav-item[data-open]').forEach((other) => other !== item && setOpen(other, false));
};
// Delegation survives a header section being replaced in the theme editor; no old header is retained.
document.addEventListener('pointerover', reach);
document.addEventListener('focusin', reach);
document.addEventListener('click', (event) => {
  if (!event.target.closest('.header-links')) return;
  const item = event.target.closest('.nav-toggle')?.closest('.nav-item');
  if (item) setOpen(item, !item.hasAttribute('data-open'));
});
document.addEventListener('focusout', (event) => {
  if (!event.target.closest('.header-links')) return;
  const item = event.target.closest('.nav-item');
  if (item && !item.contains(event.relatedTarget)) {
    setOpen(item, false);
    item.removeAttribute('data-dismissed');
  }
});
document.addEventListener('pointerout', (event) => {
  if (!event.target.closest('.header-links')) return;
  const item = event.target.closest('.nav-item[data-dismissed]');
  if (item && !item.contains(event.relatedTarget)) item.removeAttribute('data-dismissed');
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const item =
    document.activeElement?.closest('.header-links .nav-item') ||
    document.querySelector('.header-links .nav-item:hover');
  if (!item) return;
  const back = item.hasAttribute('data-open') || item.querySelector('.mega')?.contains(document.activeElement);
  if (item.hasAttribute('data-open')) setOpen(item, false);
  else item.setAttribute('data-dismissed', '');
  if (back) item.querySelector('.nav-toggle').focus();
});

/* The phone menu's departments are tabs: one list at a time; the arrow keys move between them, Home and End to the
   ends. A link to the page itself only closes the menu. */
document.addEventListener('click', (event) => {
  const tab = event.target.closest('[data-menu-tabs] [role=tab]');
  if (tab) selectTab(tab);
  const here = event.target.closest('.menu a[aria-current=page]');
  if (here && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
    event.preventDefault();
    closePanel();
  }
});
document.addEventListener('keydown', (event) => {
  const tab = event.target.closest?.('[data-menu-tabs] [role=tab]');
  if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const tabs = [...tab.parentElement.children];
  const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
  const next = step
    ? tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length]
    : tabs[event.key === 'Home' ? 0 : tabs.length - 1];
  selectTab(next);
  next.focus();
});
function selectTab(tab) {
  tab.parentElement.querySelectorAll('[role=tab]').forEach((other) => {
    const on = other === tab;
    other.setAttribute('aria-selected', on);
    other.tabIndex = on ? 0 : -1;
    document.getElementById(other.getAttribute('aria-controls')).hidden = !on;
  });
}

window.Laley = {
  enhancePanelLinks,
  openPanel,
  closePanel,
  refreshCartCount,
  openQuick,
  showAdded,
  upsellFor,
  besidePicture,
  openCart,
  syncFavorites,
};
